import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  X,
  ShieldCheck,
  Layers,
  Globe,
  HardDrive,
  Sparkles,
  Inbox,
  AlertCircle,
  History,
  Loader2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

/**
 * Modal that renders the five Phase 6 override caps for one user.
 *
 * Input semantics (mirrors the server):
 *   - Empty textbox → null (clear override, fall back to plan default)
 *   - "-1"         → unlimited
 *   - "0"           → hard freeze (valid)
 *   - Any other integer ≥ -1 → that explicit cap
 *
 * The reason field is a single string attached to the batched PATCH,
 * not per-field. The audit log carries the same reason on every row
 * produced by the same PATCH so the operator can later grep for
 * "bumped for launch".
 */

export interface OverridesModalUser {
  id: string;
  email: string;
  name: string;
  subscription: string;
}

interface OverridesModalProps {
  user: OverridesModalUser;
  onClose: () => void;
  onSaved: () => void;
}

type FieldKey =
  | 'pageLimit'
  | 'customDomainLimit'
  | 'storageMb'
  | 'aiGensMonthly'
  | 'submissionsMonthly';

interface OverridesResponse {
  userId: string;
  subscription: string;
  overrides: Record<FieldKey, number | null>;
  reason: string | null;
  setAt: string | null;
  setBy: string | null;
  auditLog: Array<{
    id: string;
    field: string;
    oldValue: number | null;
    newValue: number | null;
    reason: string | null;
    adminId: string | null;
    adminEmail: string | null;
    createdAt: string;
  }>;
}

type Inputs = Record<FieldKey, string>;

const FIELD_META: Array<{
  key: FieldKey;
  icon: React.ComponentType<{ className?: string }>;
  tKey: string;
}> = [
  { key: 'pageLimit', icon: Layers, tKey: 'overrides.fields.pageLimit' },
  { key: 'customDomainLimit', icon: Globe, tKey: 'overrides.fields.customDomainLimit' },
  { key: 'storageMb', icon: HardDrive, tKey: 'overrides.fields.storageMb' },
  { key: 'aiGensMonthly', icon: Sparkles, tKey: 'overrides.fields.aiGensMonthly' },
  { key: 'submissionsMonthly', icon: Inbox, tKey: 'overrides.fields.submissionsMonthly' },
];

function numberToInput(v: number | null | undefined): string {
  if (v === null || v === undefined) return '';
  return String(v);
}

/**
 * Parse the raw textbox string the admin typed. Returns:
 *   { kind: 'clear' }        — empty input → clear override
 *   { kind: 'value', value } — integer ≥ -1
 *   { kind: 'invalid' }      — reject on save
 */
function parseInput(raw: string): { kind: 'clear' } | { kind: 'value'; value: number } | { kind: 'invalid' } {
  const trimmed = raw.trim();
  if (trimmed === '') return { kind: 'clear' };
  if (!/^-?\d+$/.test(trimmed)) return { kind: 'invalid' };
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < -1) return { kind: 'invalid' };
  return { kind: 'value', value: n };
}

function formatValue(v: number | null | undefined, unlimitedLabel: string, defaultLabel: string): string {
  if (v === null || v === undefined) return defaultLabel;
  if (v === -1) return unlimitedLabel;
  return String(v);
}

export const OverridesModal: React.FC<OverridesModalProps> = ({ user, onClose, onSaved }) => {
  const { t } = useTranslation('admin');
  const token = useMemo(() => localStorage.getItem('token'), []);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [notifyUser, setNotifyUser] = useState(false);
  const [data, setData] = useState<OverridesResponse | null>(null);
  const [inputs, setInputs] = useState<Inputs>({
    pageLimit: '',
    customDomainLimit: '',
    storageMb: '',
    aiGensMonthly: '',
    submissionsMonthly: '',
  });
  const [reason, setReason] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldKey, string>>>({});

  const fetchOverrides = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin/users/${user.id}/overrides`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(String(res.status));
      const json = (await res.json()) as OverridesResponse;
      setData(json);
      setInputs({
        pageLimit: numberToInput(json.overrides.pageLimit),
        customDomainLimit: numberToInput(json.overrides.customDomainLimit),
        storageMb: numberToInput(json.overrides.storageMb),
        aiGensMonthly: numberToInput(json.overrides.aiGensMonthly),
        submissionsMonthly: numberToInput(json.overrides.submissionsMonthly),
      });
      setReason(json.reason ?? '');
    } catch (err) {
      console.error('[overrides] fetch failed', err);
      toast.error(t('overrides.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [user.id, token, t]);

  useEffect(() => {
    fetchOverrides();
  }, [fetchOverrides]);

  const handleSave = useCallback(async () => {
    if (!token || !data) return;

    // Validate every field the admin touched before hitting the API.
    const errors: Partial<Record<FieldKey, string>> = {};
    const payload: Record<string, number | string | boolean | null> = {};

    for (const meta of FIELD_META) {
      const raw = inputs[meta.key];
      const parsed = parseInput(raw);
      if (parsed.kind === 'invalid') {
        errors[meta.key] = t('overrides.errors.invalidNumber');
        continue;
      }
      const current = data.overrides[meta.key];
      const next = parsed.kind === 'clear' ? null : parsed.value;
      // Only send fields that actually changed — reduces audit-log
      // noise when the admin just wanted to bump one cap.
      if (current !== next) {
        payload[meta.key] = next;
      }
    }

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    if (Object.keys(payload).length === 0) {
      toast(t('overrides.toasts.noChanges'), { icon: 'ℹ️' });
      return;
    }

    payload.reason = reason.trim() || null;
    payload.notifyUser = notifyUser;

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/admin/users/${user.id}/overrides`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || String(res.status));
      }
      toast.success(t('overrides.toasts.saved'));
      onSaved();
      // Refresh so the audit log picks up the new entries and the
      // form reflects the "current" values (in case server coerced
      // anything — today it doesn't, but stay defensive).
      await fetchOverrides();
    } catch (err) {
      console.error('[overrides] save failed', err);
      toast.error(t('overrides.toasts.saveFailed'));
    } finally {
      setSaving(false);
    }
  }, [token, data, inputs, reason, notifyUser, user.id, t, onSaved, fetchOverrides]);

  const handleClearAll = useCallback(() => {
    setInputs({
      pageLimit: '',
      customDomainLimit: '',
      storageMb: '',
      aiGensMonthly: '',
      submissionsMonthly: '',
    });
    setFieldErrors({});
  }, []);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl p-0 max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-xl font-bold text-gray-900 truncate">
                {t('overrides.title')}
              </h2>
              <p className="text-sm text-gray-500 truncate">
                {user.name} · {user.email}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {t('overrides.subtitle')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              {t('overrides.loading')}
            </div>
          ) : (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">{t('overrides.legend.title')}</p>
                  <p className="text-xs mt-0.5">{t('overrides.legend.body')}</p>
                </div>
              </div>

              <div className="space-y-3">
                {FIELD_META.map((meta) => {
                  const Icon = meta.icon;
                  const currentOverride = data?.overrides[meta.key] ?? null;
                  const err = fieldErrors[meta.key];
                  return (
                    <div key={meta.key}>
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1.5">
                        <Icon className="w-4 h-4 text-gray-500" />
                        {t(meta.tKey)}
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder={t('overrides.placeholder.useDefault')}
                        value={inputs[meta.key]}
                        onChange={(e) =>
                          setInputs((prev) => ({ ...prev, [meta.key]: e.target.value }))
                        }
                        className={`w-full px-3 py-2 border rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 ${
                          err
                            ? 'border-red-300 focus:ring-red-400'
                            : 'border-gray-300 focus:ring-blue-500'
                        }`}
                      />
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs text-gray-500">
                          {t('overrides.current')}:{' '}
                          <span className="font-mono">
                            {formatValue(
                              currentOverride,
                              t('overrides.unlimited'),
                              t('overrides.inherited'),
                            )}
                          </span>
                        </p>
                        {err && <p className="text-xs text-red-600">{err}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t('overrides.reason')}
                </label>
                <textarea
                  rows={2}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t('overrides.reasonPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={notifyUser}
                  onChange={(e) => setNotifyUser(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span>{t('overrides.notifyUser')}</span>
              </label>

              {/* Audit log */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowAudit((v) => !v)}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  <History className="w-4 h-4" />
                  {showAudit
                    ? t('overrides.audit.hide')
                    : t('overrides.audit.show', { count: data?.auditLog.length ?? 0 })}
                </button>
                {showAudit && data && data.auditLog.length > 0 && (
                  <div className="mt-3 border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-60 overflow-y-auto">
                    {data.auditLog.slice(0, 20).map((row) => (
                      <div key={row.id} className="px-3 py-2 text-xs flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-gray-700 truncate">
                            {row.field}
                            {': '}
                            <span className="text-gray-500">
                              {row.oldValue === null ? 'null' : row.oldValue}
                            </span>{' '}
                            →{' '}
                            <span className="text-gray-900 font-medium">
                              {row.newValue === null ? 'null' : row.newValue}
                            </span>
                          </p>
                          {row.reason && (
                            <p className="text-gray-500 italic mt-0.5 line-clamp-2">
                              "{row.reason}"
                            </p>
                          )}
                          <p className="text-gray-400 mt-0.5">
                            {row.adminEmail || t('overrides.audit.unknownAdmin')} ·{' '}
                            {new Date(row.createdAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {showAudit && data && data.auditLog.length === 0 && (
                  <p className="mt-2 text-xs text-gray-500 italic">
                    {t('overrides.audit.empty')}
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={handleClearAll}
            disabled={loading || saving}
            className="text-sm text-gray-600 hover:text-red-600 transition-colors disabled:opacity-50"
          >
            {t('overrides.clearAll')}
          </button>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium text-sm"
            >
              {t('overrides.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={loading || saving}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg font-medium text-sm flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {t('overrides.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
